// src/hooks/useFirestore.js
import { useState, useEffect } from 'react';
import {
  collection, doc, addDoc, updateDoc, deleteDoc, onSnapshot,
  query, where, orderBy, limit, serverTimestamp, getDocs, setDoc
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, auth, storage } from '../firebase';

// Generic hook: listen to a user-scoped collection
export function useCollection(collectionName, queryConstraints = [], deps = []) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const user = auth.currentUser;
    if (!user) { setLoading(false); return; }

    const ref = collection(db, 'users', user.uid, collectionName);
    const q = queryConstraints.length > 0 ? query(ref, ...queryConstraints) : query(ref, orderBy('createdAt', 'desc'));
    
    const unsub = onSnapshot(q, (snap) => {
      setData(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
    }, (err) => {
      console.error(`Error listening to ${collectionName}:`, err);
      setLoading(false);
    });

    return unsub;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collectionName, ...deps]);

  return { data, loading };
}

// Add document to user-scoped collection
export async function addToCollection(collectionName, data) {
  const user = auth.currentUser;
  if (!user) throw new Error('Not authenticated');
  const ref = collection(db, 'users', user.uid, collectionName);
  return addDoc(ref, { ...data, createdAt: serverTimestamp(), updatedAt: serverTimestamp() });
}

// Update document
export async function updateInCollection(collectionName, docId, data) {
  const user = auth.currentUser;
  if (!user) throw new Error('Not authenticated');
  const ref = doc(db, 'users', user.uid, collectionName, docId);
  return updateDoc(ref, { ...data, updatedAt: serverTimestamp() });
}

// Delete document
export async function deleteFromCollection(collectionName, docId) {
  const user = auth.currentUser;
  if (!user) throw new Error('Not authenticated');
  const ref = doc(db, 'users', user.uid, collectionName, docId);
  return deleteDoc(ref);
}

// Get or set user settings (top-level doc)
export function useSettings() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const user = auth.currentUser;
    if (!user) { setLoading(false); return; }

    const ref = doc(db, 'settings', user.uid);
    const unsub = onSnapshot(ref, (snap) => {
      if (snap.exists()) {
        setSettings({ id: snap.id, ...snap.data() });
      } else {
        const defaults = {
          monthlyIncomeTarget: 450000,
          savingsTarget: 200000,
          shoppingCap: 40000,
          lifestyleCap: 15000,
          month: new Date().toISOString().slice(0, 7),
          lastMonthPerformance: { income: 0, spend: 0, savings: 0 },
          categoryBudgets: {
            Food: 15000, Shopping: 10000, Transport: 5000,
            Entertainment: 5000, Bills: 20000, Health: 5000,
            Education: 10000, Other: 10000,
          },
          toggles: {
            biometricPrompt: true, autoLockVault: true, cloudBackups: false,
            pacingAlerts: true, anomalyDetection: true,
          },
        };
        setDoc(ref, defaults);
        setSettings(defaults);
      }
      setLoading(false);
    });
    return unsub;
  }, []);

  const updateSettings = async (data) => {
    const user = auth.currentUser;
    if (!user) return;
    const ref = doc(db, 'settings', user.uid);
    await setDoc(ref, { ...data, updatedAt: serverTimestamp() }, { merge: true });
  };

  return { settings, loading, updateSettings };
}

// ── Collection Hooks ────────────────────────────────────────────

export function useGoals() {
  return useCollection('goals', [orderBy('createdAt', 'desc')]);
}

export function useAssets() {
  return useCollection('assets');
}

export function useLiabilities() {
  return useCollection('liabilities');
}

export function useEMIs() {
  return useCollection('emis', [orderBy('dueDate', 'asc')]);
}

export function useVault() {
  return useCollection('vault');
}

export function useWatchlist() {
  return useCollection('watchlist');
}

export function useOrders() {
  return useCollection('orders', [orderBy('orderDate', 'desc')]);
}

export function useNetWorthHistory() {
  return useCollection('netWorthHistory', [orderBy('month', 'asc')]);
}

export function useFixedExpenses() {
  return useCollection('fixedExpenses', [orderBy('dueDay', 'asc')]);
}

export function useOperationalExpenses(monthFilter = null) {
  const constraints = [];
  if (monthFilter) {
    const [year, month] = monthFilter.split('-').map(Number);
    const start = new Date(year, month - 1, 1).toISOString();
    const end = new Date(year, month, 0, 23, 59, 59).toISOString();
    constraints.push(where('date', '>=', start), where('date', '<=', end), orderBy('date', 'desc'));
  } else {
    constraints.push(orderBy('date', 'desc'), limit(100));
  }
  return useCollection('operationalExpenses', constraints, [monthFilter]);
}

export function useSpendingTargets() {
  const [targets, setTargets] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const user = auth.currentUser;
    if (!user) { setLoading(false); return; }
    const ref = doc(db, 'users', user.uid, 'spendingTargets', 'current');
    const unsub = onSnapshot(ref, (snap) => {
      if (snap.exists()) {
        setTargets({ id: snap.id, ...snap.data() });
      } else {
        const defaults = { dailyCap: 0, weeklyCap: 0, monthlyCap: 0 };
        setDoc(ref, { ...defaults, updatedAt: serverTimestamp() });
        setTargets(defaults);
      }
      setLoading(false);
    });
    return unsub;
  }, []);

  const updateTargets = async (data) => {
    const user = auth.currentUser;
    if (!user) return;
    const ref = doc(db, 'users', user.uid, 'spendingTargets', 'current');
    await setDoc(ref, { ...data, updatedAt: serverTimestamp() }, { merge: true });
  };

  return { targets, loading, updateTargets };
}

export function useGamification() {
  const [gamification, setGamification] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const user = auth.currentUser;
    if (!user) { setLoading(false); return; }
    const ref = doc(db, 'users', user.uid, 'gamification', 'stats');
    const unsub = onSnapshot(ref, (snap) => {
      if (snap.exists()) {
        setGamification({ id: snap.id, ...snap.data() });
      } else {
        const defaults = { currentStreak: 0, longestStreak: 0, lastStreakDate: null, badges: [], badgeEarnedDates: {} };
        setDoc(ref, { ...defaults, updatedAt: serverTimestamp() });
        setGamification(defaults);
      }
      setLoading(false);
    });
    return unsub;
  }, []);

  const updateGamification = async (data) => {
    const user = auth.currentUser;
    if (!user) return;
    const ref = doc(db, 'users', user.uid, 'gamification', 'stats');
    await setDoc(ref, { ...data, updatedAt: serverTimestamp() }, { merge: true });
  };

  return { gamification, loading, updateGamification };
}

export function useIncomeTargets() {
  const [targets, setTargets] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const user = auth.currentUser;
    if (!user) { setLoading(false); return; }
    const ref = doc(db, 'users', user.uid, 'incomeTargets', 'current');
    const unsub = onSnapshot(ref, (snap) => {
      if (snap.exists()) {
        setTargets({ id: snap.id, ...snap.data() });
      } else {
        const defaults = { dailyTarget: 0 };
        setDoc(ref, { ...defaults, updatedAt: serverTimestamp() });
        setTargets(defaults);
      }
      setLoading(false);
    });
    return unsub;
  }, []);

  const updateTargets = async (data) => {
    const user = auth.currentUser;
    if (!user) return;
    const ref = doc(db, 'users', user.uid, 'incomeTargets', 'current');
    await setDoc(ref, { ...data, updatedAt: serverTimestamp() }, { merge: true });
  };

  return { targets, loading, updateTargets };
}

// Bank balances (from parsed statement closing balances)
export function useBankBalances() {
  return useCollection('bankBalances');
}

// Save/update a bank's closing balance
export async function saveBankBalance(bankName, balance, date) {
  const user = auth.currentUser;
  if (!user) throw new Error('Not authenticated');
  const ref = doc(db, 'users', user.uid, 'bankBalances', bankName.replace(/\s+/g, '_'));
  return setDoc(ref, { bank: bankName, balance, date, updatedAt: serverTimestamp() }, { merge: true });
}

// Recent transactions for last N months (for Dashboard charts)
export function useRecentTransactions(monthsBack = 6) {
  const cutoff = new Date();
  cutoff.setMonth(cutoff.getMonth() - monthsBack);
  cutoff.setDate(1);
  const cutoffStr = cutoff.toISOString();
  return useCollection('transactions', [where('date', '>=', cutoffStr), orderBy('date', 'desc')], [monthsBack]);
}

// ── Bank Transaction Helpers ────────────────────────────────────

// Listen to transactions collection with optional month filter (e.g. "2026-02")
export function useTransactions(monthFilter = null) {
  const constraints = [];
  if (monthFilter) {
    const [year, month] = monthFilter.split('-').map(Number);
    const start = new Date(year, month - 1, 1).toISOString();
    const end = new Date(year, month, 0, 23, 59, 59).toISOString();
    constraints.push(where('date', '>=', start), where('date', '<=', end), orderBy('date', 'desc'));
  } else {
    constraints.push(orderBy('date', 'desc'), limit(200));
  }
  return useCollection('transactions', constraints, [monthFilter]);
}

// Generate a dedup hash for a transaction
function txnHash(txn) {
  return `${txn.date}_${txn.amount}_${txn.description}`.replace(/\s+/g, '_').toLowerCase();
}

// Batch-add transactions with deduplication
export async function addTransactions(transactions) {
  const user = auth.currentUser;
  if (!user) throw new Error('Not authenticated');

  // Fetch existing hashes for dedup
  const ref = collection(db, 'users', user.uid, 'transactions');
  const existing = await getDocs(ref);
  const existingHashes = new Set();
  existing.forEach(d => {
    const data = d.data();
    existingHashes.add(txnHash(data));
  });

  const newTxns = transactions.filter(t => !existingHashes.has(txnHash(t)));
  const ts = serverTimestamp();

  // Batch write in chunks of 500 (Firestore limit)
  for (let i = 0; i < newTxns.length; i++) {
    await addDoc(ref, { ...newTxns[i], createdAt: ts, updatedAt: ts });
  }

  return { added: newTxns.length, duplicates: transactions.length - newTxns.length };
}

// Check if yesterday's data has been uploaded
export function useDailyUploadStatus() {
  const [status, setStatus] = useState({ uploaded: false, lastUploadDate: null, loading: true });

  useEffect(() => {
    const user = auth.currentUser;
    if (!user) { setStatus(s => ({ ...s, loading: false })); return; }

    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    const yStart = new Date(yesterday.getFullYear(), yesterday.getMonth(), yesterday.getDate()).toISOString();
    const yEnd = new Date(yesterday.getFullYear(), yesterday.getMonth(), yesterday.getDate(), 23, 59, 59).toISOString();

    const ref = collection(db, 'users', user.uid, 'transactions');
    const q = query(ref, where('date', '>=', yStart), where('date', '<=', yEnd), limit(1));

    const unsub = onSnapshot(q, (snap) => {
      setStatus({
        uploaded: !snap.empty,
        lastUploadDate: snap.empty ? null : snap.docs[0].data().date,
        loading: false,
      });
    });
    return unsub;
  }, []);

  return status;
}

// Update a single transaction's category
export async function updateTransactionCategory(docId, category, categoryColor) {
  return updateInCollection('transactions', docId, { category, categoryColor });
}

// Delete all documents in a user-scoped collection
export async function clearCollection(collectionName) {
  const user = auth.currentUser;
  if (!user) throw new Error('Not authenticated');
  const colRef = collection(db, 'users', user.uid, collectionName);
  const snap = await getDocs(colRef);
  const promises = snap.docs.map(d => deleteDoc(d.ref));
  await Promise.all(promises);
  return snap.size;
}

// ── Cash In Hand (singleton doc) ────────────────────────────────

export function useCashInHand() {
  const [cashInHand, setCashInHand] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const user = auth.currentUser;
    if (!user) { setLoading(false); return; }
    const docRef = doc(db, 'users', user.uid, 'cashInHand', 'current');
    const unsub = onSnapshot(docRef, (snap) => {
      if (snap.exists()) {
        setCashInHand({ id: snap.id, ...snap.data() });
      } else {
        const defaults = { balance: 0 };
        setDoc(docRef, { ...defaults, updatedAt: serverTimestamp() });
        setCashInHand(defaults);
      }
      setLoading(false);
    });
    return unsub;
  }, []);

  const updateCashInHand = async (data) => {
    const user = auth.currentUser;
    if (!user) return;
    const docRef = doc(db, 'users', user.uid, 'cashInHand', 'current');
    await setDoc(docRef, { ...data, updatedAt: serverTimestamp() }, { merge: true });
  };

  return { cashInHand, loading, updateCashInHand };
}

// ── Wishlist / Vision Board ─────────────────────────────────────

export function useWishlistItems() {
  return useCollection('wishlistItems', [orderBy('createdAt', 'desc')]);
}

// Upload wishlist item image to Firebase Storage
export async function uploadWishlistImage(itemId, file) {
  const user = auth.currentUser;
  if (!user) throw new Error('Not authenticated');
  const path = `users/${user.uid}/wishlist/${itemId}/${file.name}`;
  const storageRef = ref(storage, path);
  await uploadBytes(storageRef, file);
  const imageUrl = await getDownloadURL(storageRef);
  return { imageUrl, imagePath: path };
}

// Delete wishlist item image from Firebase Storage
export async function deleteWishlistImage(imagePath) {
  if (!imagePath) return;
  try {
    await deleteObject(ref(storage, imagePath));
  } catch (e) {
    console.warn('deleteWishlistImage:', e.message);
  }
}

// ── Holidays ────────────────────────────────────────────────────

export function useHolidays() {
  return useCollection('holidays', [orderBy('date', 'asc')]);
}
